<template>
  <div class="mx-auto mw-700">
    <h-loading v-if="loading" />

    <v-form v-show="!loading" ref="hostForm" v-model="valid">
      <h3 class="mb-3">{{ $t('language.location') }}</h3>

      <v-row >
        <v-col cols="12" sm="4" class="pb-0">
          <HCountrySelect
            v-model="country"
            :country="country"
            :default-value="country"
          />
        </v-col>
        <v-col cols="6" sm="5" class="pb-0">
          <HLanguageSelect
            v-model="language"
            :country="country"
            :default-value="language"
          />
        </v-col>
        <v-col cols="6" sm="3" class="pb-0">
          <HCurrencySelect
            v-model="currency"
            :country="country"
            :default-value="currency"
            :disabled="true"
          />
        </v-col>
      </v-row>

      <HCitySelect
        v-model="city"
        :country="country"
        :defaultValue="host.city"
      />

      <v-divider class="mb-10" />

      <h3 class="mb-3">{{ $t('host.titles.hostInfo') }}</h3>

      <v-row>
        <v-col cols="10">
          <v-text-field
            v-model="name"
            :label="`${$t('host.label.name')}*`"
            :rules="required"
            outlined
          />
        </v-col>
        <v-col cols="1" class="mt-2">
          <HostFavorite
            :starred="starred"
            @setStarred="setStarred"
          />
        </v-col>
        <v-col cols="1" class="mt-2">
          <HostMonitored
            :monitored="monitored"
            @setMonitored="setMonitored"
          />
        </v-col>
      </v-row>

      <v-text-field
        v-model="username"
        :label="`${$t('host.label.username')}*`"
        :rules="emailRule"
        autocomplete="new-email"
        outlined
      />

      <HPhone
        v-model="phone"
        :default-value="phone"
        :rules="required"
        :country="country"
      />

      <HDocumentID
        v-model="cpf"
        :default-value="cpf"
        :rules="cpfRule"
        :country="country"
        :label="$t('host.label.cpf')"
      />

      <v-text-field
        v-if="country === 'BR'"
        v-model="rg"
        :label="$t('host.label.rg')"
        :rules="rgRule"
        outlined
      />

      <v-text-field
        v-model="birthDate"
        :label="$t('host.label.birthDate')"
        :hint="$t('forms.dateHint')"
        :rules="[rules.birthDateCheck]"
        v-mask="'##/##/####'"
        maxlength="10"
        class="mw-200"
        outlined
      />

      <v-divider class="mb-8" />

      <v-text-field
        v-model="password"
        :label="$t('host.label.password')"
        :rules="passwordRule"
        autocomplete="new-password"
        type="password"
        outlined
      />

      <v-divider class="mb-5" />

      <v-switch :label="$t('host.label.tester')" v-model="tester" />

      <v-divider class="mt-2 mb-8" />

      <HPartnerSelect v-model="partner" :default-value="partner" />

      <v-spacer class="mb-5" />

      <HostServiceTypeSelect v-model="service_type" :rules="required" />

      <v-divider class="mt-1 mb-8" />
      <h3 class="mb-3">{{ $t('host.titles.billingData') }}</h3>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model="invoice_company_name"
            :label="$t('host.label.invoiceCompanyName')"
            outlined
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-text-field
            v-model="cnpj"
            :label="$t('host.label.cnpj')"
            :rules="[cnpjOptionalRule]"
            v-mask="'##.###.###/####-##'"
            maxlength="18"
            outlined
            clearable
          />
        </v-col>

        <v-col cols="12">
          <v-autocomplete
            v-model="tax_regime"
            :items="taxRegimeItems"
            :label="$t('host.label.taxRegime')"
            item-text="text"
            item-value="value"
            clearable
            outlined
          />
        </v-col>
      </v-row>

      <div class="d-flex justify-end mt-5">
        <v-btn
          elevation="0"
          color="primary"
          :loading="isLoading"
          @click="() => update(false)"
          large
        >
          {{ $t('create.save') }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script>
import BirthDatePicker from '@/components/app/BirthDatePicker'
import HCheckbox from '@/components/app/HCheckbox'
import HAutocomplete from '@/components/app/HAutocomplete'
import HostServiceTypeSelect from '@/components/host/HostServiceTypeSelect'
import HostFavorite from './components/HostFavorite'
import HostMonitored from './components/HostMonitored'
import HDocumentID from '@/components/app/HDocumentID'
import HPhone from '../app/HPhone'
import HCountrySelect from '../app/HCountrySelect'
import HCurrencySelect from '../app/HCurrencySelect'
import HLanguageSelect from '../app/HLanguageSelect'
import HCitySelect from '../app/HCitySelect'
import HPartnerSelect from '../app/HPartnerSelect'

import { formatDocument } from '@/util/document'
import { catalogPhone } from '@/util/catalog'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { birthDateCheck } from '@/util/errorHandling'
import { unmaskText } from '@/util/helpers'
import {
  required,
  emailRule,
  passwordRule,
  cpfRule,
  rgRule,
  phoneRule,
} from '@/util/defaultValidationRules'

export default {
  name: 'HostUpdate',

  components: {
    HostServiceTypeSelect,
    HCheckbox,
    HAutocomplete,
    BirthDatePicker,
    HostFavorite,
    HostMonitored,
    HDocumentID,
    HPhone,
    HCountrySelect,
    HCurrencySelect,
    HLanguageSelect,
    HCitySelect,
    HPartnerSelect,
  },

  props: {
    closeModal: Function,

    loading: {
      type: Boolean,
      default: false,
    },
  },

  watch: {
    host(host) {
      if (!host) return

      this.$nextTick(() => {
        this.country = host.country ?? 'BR'
        this.currency = host.currency ?? 'BRAZILIAN_REAL'
        this.language = host.language ?? 'PT_BR'
      })

      this.$nextTick(() => {
        this.starred = host.starred
        this.monitored = host.monitored
        this.parseBirthDateFill(host.birth_date)
        this.city = host.city?.id
        this.cpf = host.cpf
        this.rg = host.rg
        this.name = host.name
        this.phone = host.phone
        this.service_type = host.service_type
        this.tester = host.tester
        this.username = host.username
        this.partner = host.partner
        this.invoice_company_name = host.invoice_company_name || null
        this.cnpj = host.cnpj || null
        this.tax_regime = host.tax_regime || null
      })
    }
  },

  data() {
    return {
      valid: false,

      starred: false,
      monitored: false,
      birthDate: null,
      city: null,
      cpf: null,
      rg: null,
      name: null,
      password: null,
      phone: null,
      service_type: null,
      tester: null,
      username: null,
      enabled: true,
      currency: null,
      country: null,
      language: null,
      partner: null,
      invoice_company_name: null,
      cnpj: null,
      tax_regime: null,
      taxRegimes: ['MEI', 'SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL'],
      formatDocument,

      rules: {
        birthDateCheck,
      }
    }
  },

  computed: {
    ...mapGetters(['host', 'cities', 'isLoading', 'partners']),

    hasServiceTypeSelected() {
      return this.host?.service_type?.length > 0
    },

    required() {
      return required
    },

    emailRule() {
      return this.required.concat(emailRule)
    },

    passwordRule() {
      return passwordRule
    },

    cpfRule() {
      return cpfRule
    },

    rgRule() {
      return rgRule
    },

    phoneRule() {
      return this.required.concat(phoneRule)
    },

    taxRegimeItems() {
      return this.taxRegimes.map((value) => ({
        value,
        text: this.$t(`host.taxRegime.${value}`) || value.replace('_', ' '),
      }))
    },
  },

  methods: {
    ...mapActions([
      'fetchEnabledCitiesAdmin',
      'showSuccessMessage',
      'showErrorMessage',
      'updateHost',
      'fetchHosts',
      'fetchPartners',
    ]),

    ...mapMutations(['setIsLoading', 'setHost', 'setIsLoadingChangeStatus']),

    formatPhone: catalogPhone,

    async update(doNotCloseModal) {
      if (!this.$refs.hostForm.validate()) return

      this.setIsLoading(true)

      const body = {
        birth_date: this.parseBirthDate(this.birthDate),
        city_id: this.city,
        cpf: unmaskText(this.cpf),
        rg: this.rg,
        name: this.name,
        password: this.password,
        phone: this.phone,
        service_type: this.service_type,
        tester: this.tester,
        username: this.username,
        starred: this.starred,
        monitored: this.monitored,
        enabled: this.enabled,
        country: this.country,
        currency: this.currency,
        language: this.language,
        partner_id: this.partner?.id ?? null,
        invoice_company_name: this.invoice_company_name,
        cnpj: unmaskText(this.cnpj),
        tax_regime: this.tax_regime,
      }

      this.updateHost({ body, id: this.host.id })
        .then(async () => {
          this.showSuccessMessage('Host atualizado com sucesso!')
          await this.fetchHosts()

        })
        .then(() => {
          if (!doNotCloseModal) {
            this.closeModal()
            this.setHost({})
          }
        })
        .catch(({ data }) => {
          if (data && data?.errors) {
            let errorMessage = ''
            data.errors.forEach((error) => {
              errorMessage += `${this.$t('host.label.' + error.field)}: ${
                error.message
              }; `
            })
            this.showErrorMessage(errorMessage)
          } else {
            this.showErrorMessage(data.message)
          }
        })
        .finally(() => {
          this.setIsLoading(false)
          this.setIsLoadingChangeStatus(false)
        })
    },

    parseBirthDate(date) {
      if (!date) return

      const splittedDate = date.split('/')
      const parsedDate = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`
      return parsedDate + 'T12:00:00-03:00'
    },

    parseBirthDateFill(date) {
      if (!date) return

      const splittedDate = date.split('-')
      if (splittedDate.length <= 2) return
      const parsedDate = `${splittedDate[2]?.substring(0,2)}/${splittedDate[1]}/${splittedDate[0]}`
      this.$nextTick(() => this.birthDate = parsedDate)
    },

    setStarred(status) {
      this.starred = status
    },

    setMonitored(status) {
    this.monitored = status
    },
  },
}
</script>
